import React from 'react'
import { Box, CircularProgress, LinearProgress, Typography, Button } from '@mui/material'
import { useFunMode } from 'contexts'
import { FunMediaPanel, MiniGamePanel } from 'components/fun'
import FullscreenOverlay from 'components/common/FullscreenOverlay'
import BrowserExportNotification from './ui/BrowserExportNotification'
import useFunMediaPlaylist from './hooks/useFunMediaPlaylist'
import useExportNotifications from './hooks/useExportNotifications'
import useExportMetrics from './hooks/useExportMetrics'
import type { DisplayMode } from './types'

type ExportOverlayProps = {
  open: boolean
  progress: {
    loaded: number
    total: number
  }
  onCancel?: () => void
}

const ExportOverlay = ({ open, progress, onCancel }: ExportOverlayProps) => {
  const { funModeEnabled } = useFunMode()
  const [displayMode, setDisplayMode] = React.useState<DisplayMode>('gifs')
  const titleId = React.useId()
  const subtitleId = React.useId()

  const { hasFirstResponse, percent, title, subtitle, details } = useExportMetrics(open, progress)
  const { currentMedia, showGifPanel, showGamePanel } = useFunMediaPlaylist({
    open,
    hasFirstResponse,
    funModeEnabled,
    displayMode,
  })

  const {
    notifyPanelOpen,
    notifySupported,
    notifyPermission,
    requestingPermission,
    notifyError,
    notifyEnabled,
    handleOpenNotifyPanel,
    handleEnableNotify,
  } = useExportNotifications(open)

  if (!open) return null

  const toggleModeLabel =
    displayMode === 'gifs' ? 'Je préfère jouer' : 'Je préfère voir les gifs'

  const handleToggleMode = () => {
    setDisplayMode((mode) => (mode === 'gifs' ? 'game' : 'gifs'))
  }

  return (
    <FullscreenOverlay
      open={open}
      onClose={onCancel}
      ariaLabelledBy={titleId}
      ariaDescribedBy={subtitleId}
      contentSx={{
        maxWidth: 1100,
        maxHeight: '90vh',
        overflowY: 'auto',
    }}
    >
      <Typography id={titleId} variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>

      <Typography id={subtitleId} variant="body2" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>

        {progress.total > 0 && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress variant="determinate" value={percent} />
          </Box>
        )}

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 2,
          opacity: 0.75,
        }}
      >
        {details}
      </Typography>

      {showGifPanel && currentMedia && <FunMediaPanel media={currentMedia} />}

      {showGamePanel && <MiniGamePanel running={open} />}

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        {funModeEnabled && (
          <Button variant="outlined" onClick={handleToggleMode}>
            {toggleModeLabel}
          </Button>
        )}

        <Button
          variant="outlined"
          onClick={handleOpenNotifyPanel}
          disabled={requestingPermission}
        >
          {notifyEnabled ? 'Notifications activées' : 'Je veux être prévenu'}
        </Button>
      </Box>

      <BrowserExportNotification
        open={notifyPanelOpen}
        supported={notifySupported}
        permission={notifyPermission}
        requesting={requestingPermission}
        error={notifyError}
        onEnable={handleEnableNotify}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 2,
          mb: onCancel ? 2 : 0,
        }}
      >
        <CircularProgress />
      </Box>

      {onCancel && (
        <Button onClick={onCancel} variant="text">
          Annuler
        </Button>
      )}
    </FullscreenOverlay>
  )
}

export default ExportOverlay
